import {
  type BranchAndTagCountData,
  BranchAndTagCountProvider,
} from '@github-ui/code-view-shared/contexts/BranchAndTagContext'
import {CommitInfoProvider} from '@github-ui/code-view-shared/contexts/CommitInfoContext'
import {CurrentTreeProvider} from '@github-ui/code-view-shared/contexts/CurrentTreeContext'
import {LatestCommitProvider} from '@github-ui/code-view-shared/contexts/LatestCommitContext'
import {
  type RecentlyTouchedBranches,
  RecentlyTouchedBranchesProvider,
} from '@github-ui/code-view-shared/contexts/RecentlyTouchedBranchesContext'
import {
  type DataRouterFileOverviewPayload,
  type DataRouterFileTreePagePayload,
  isFileOverviewPayload,
} from '@github-ui/code-view-types'
import {useCurrentRepository} from '@github-ui/current-repository'
import {queryFnFetch} from '@github-ui/react-core/future/query-fn-fetch'
import {useRouteQuery} from '@github-ui/react-core/future/use-route-query'
import {useQuery} from '@github-ui/react-query'
import {useSearchParams} from '@github-ui/react-router'
import {sendCustomMetric} from '@github-ui/stats'
import {useWebVitals} from '@github-ui/use-web-vitals'
import {useEffect} from 'react'

import {TreeBanners} from '../components/CodeViewBanners'
import FileTreeViewContent from '../components/FileTreeViewContent'
import {CodeViewHeaderDataRouter, TreeHeaderActions} from '../components/headers/CodeViewHeader'
import {useTreePaneContext} from '../contexts/TreePaneContext'
import {codeViewTreeRoute} from '../routes/code-view-tree-route'
import {CodeView} from './CodeView'
import {ReposOverviewPage} from './ReposOverviewPage'

export function CodeViewTreeEntrypoint() {
  const {data: payload} = useRouteQuery(codeViewTreeRoute, 'mainQuery')
  const {
    data: latestCommit,
    error: latestCommitError,
    isLoading: latestCommitLoading,
  } = useRouteQuery(codeViewTreeRoute, 'latestCommit')
  const {
    data: commitInfo,
    error: commitInfoError,
    isLoading: commitInfoLoading,
  } = useRouteQuery(codeViewTreeRoute, 'treeCommitInfo')

  return (
    <CommitInfoProvider commitInfo={commitInfo} error={!!commitInfoError} loading={commitInfoLoading}>
      <LatestCommitProvider latestCommit={latestCommit} error={latestCommitError} isLoading={latestCommitLoading}>
        {isFileOverviewPayload(payload) ? (
          <CodeViewRepoOnBranch payload={payload} />
        ) : (
          <CodeViewTree payload={payload} />
        )}
      </LatestCommitProvider>
    </CommitInfoProvider>
  )
}

function CodeViewRepoOnBranch({payload}: {payload: DataRouterFileOverviewPayload}) {
  const repo = useCurrentRepository()

  const recentlyTouchedBranchesQueryDeps = {
    pathname: `/${repo.ownerLogin}/${repo.name}/recently-touched-branches`,
  }
  const {data: recentlyTouchedBranches, refetch} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'recently-touched-branches', recentlyTouchedBranchesQueryDeps],
    queryFn: () => {
      return queryFnFetch<RecentlyTouchedBranches>({
        queryDeps: recentlyTouchedBranchesQueryDeps,
      })
    },
  })
  const branchAndTagCountQueryDeps = {
    pathname: `/${repo.ownerLogin}/${repo.name}/branch-and-tag-count`,
  }
  const {data: branchAndTagCount} = useQuery({
    queryKey: [repo.ownerLogin, repo.name, 'branch-and-tag-count', branchAndTagCountQueryDeps],
    queryFn: () => {
      return queryFnFetch<BranchAndTagCountData>({
        queryDeps: branchAndTagCountQueryDeps,
      })
    },
  })

  return (
    <BranchAndTagCountProvider count={branchAndTagCount}>
      <RecentlyTouchedBranchesProvider {...recentlyTouchedBranches} refetch={refetch}>
        <ReposOverviewPage payload={payload} isTree />
      </RecentlyTouchedBranchesProvider>
    </BranchAndTagCountProvider>
  )
}

function CodeViewTree({payload}: {payload: DataRouterFileTreePagePayload}) {
  const {isTreeExpanded, treeToggleElement, expandTree} = useTreePaneContext()
  const [searchParams] = useSearchParams()

  useWebVitals('hpc', event => {
    if (!event.detail.hpc) return
    sendCustomMetric({
      name: 'CODE_VIEW_TREE_HPC',
      value: event.detail.hpc,
      tags: {
        app: 'code-view',
        soft: String(event.detail.soft),
        ssr: String(event.detail.ssr),
        mechanism: String(event.detail.mechanism),
      },
    })
  })

  useWebVitals('inp', event => {
    if (!event.detail.inp) return
    sendCustomMetric({
      name: 'CODE_VIEW_TREE_INP',
      value: event.detail.inp,
      tags: {
        app: 'code-view',
        inpInteractionType: event.detail.inpInteractionType || 'unknown',
        inpEventType: event.detail.inpEventType || 'unknown',
        inpBottleneck: event.detail.inpBottleneck || 'unknown',
      },
    })
  })

  // we only want to run this once on mount to ensure the tree is expanded on search
  useEffect(() => {
    if (payload.treeExpanded && (searchParams.get('search') || searchParams.get('files'))) expandTree()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const internal = (
    <>
      <CodeViewHeaderDataRouter
        narrowActions={<TreeHeaderActions payload={payload} narrow path={payload.path} />}
        wideActions={<TreeHeaderActions payload={payload} narrow={false} path={payload.path} />}
        path={payload.path}
        commitish={payload.refInfo.name}
        modelsRepoIntegrationEnabled={payload.modelsRepoIntegrationEnabled}
      />
      <CodeView payload={payload} action="tree">
        {() => (
          <>
            <div className="react-code-view-bottom-padding">
              <TreeBanners />
            </div>
            <FileTreeViewContent tree={payload.tree} showTree={isTreeExpanded} treeToggleElement={treeToggleElement} />
          </>
        )}
      </CodeView>
    </>
  )

  return payload.error ? internal : <CurrentTreeProvider payload={payload.tree}>{internal}</CurrentTreeProvider>
}
